
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl, generateArticleSchema } from '@/utils/seoUtils';
import { blogPosts } from '@/data/blogPosts';
import { getArticleBySlug } from '@/data/blog';

const BlogPost = () => {
  const { slug, lang } = useParams<{ slug: string; lang: string }>();
  const navigate = useNavigate();
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const t = translations[currentLanguage];
  const [imageError, setImageError] = useState(false);
  
  const postFromData = blogPosts.find(post => post.slug === slug);
  const articleContent = slug ? getArticleBySlug(slug) : null;
  
  // Select a fallback image based on article topic
  const getFallbackImage = () => {
    // Choose a fallback image based on the article's slug or topic
    if (slug?.includes('video') || slug?.includes('produkt')) {
      return "https://images.unsplash.com/photo-1605810230434-7631ac76ec81?q=80&w=1200&auto=format&fit=crop";
    } else if (slug?.includes('social') || slug?.includes('marketing')) {
      return "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?q=80&w=1200&auto=format&fit=crop";
    } else if (slug?.includes('ki') || slug?.includes('ai')) {
      return "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?q=80&w=1200&auto=format&fit=crop";
    }
    // Default fallback
    return "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=1200&auto=format&fit=crop";
  };
  
  // Default fallback image if no image is available or on error
  const fallbackImage = getFallbackImage();
  
  useEffect(() => {
    if (!postFromData && !articleContent) {
      navigate(`/${currentLanguage}/blog`);
      return;
    }
    
    if (articleContent && articleContent.meta[currentLanguage]) {
      const canonicalUrl = getCanonicalUrl(`blog/${slug}`, currentLanguage);
      // Enhanced keywords with more specific terms related to the article topic
      const metaKeywords = `KI Videoproduktion, AI Storytelling, KI Videos Schweiz, ${articleContent.meta[currentLanguage].title}, ${slug.replace(/-/g, ' ')}, AI Videotechnologie, Schweizer Videoagentur, Videomarketing`;
      
      // Update SEO meta tags with more specific keywords and description
      updateMetaTags({
        title: `${articleContent.meta[currentLanguage].title} | Future Level - AI Video Marketing Schweiz`,
        description: articleContent.meta[currentLanguage].description,
        keywords: metaKeywords,
        canonicalUrl: canonicalUrl,
        language: currentLanguage,
        ogImage: articleContent.image,
        ogType: "article"
      });
      
      // Add enhanced schema.org markup for the article with more specific metadata
      generateArticleSchema(
        articleContent.meta[currentLanguage].title,
        articleContent.meta[currentLanguage].description,
        articleContent.image,
        articleContent.date,
        canonicalUrl,
        currentLanguage,
        metaKeywords
      );
    } else if (postFromData) {
      // For regular blog posts without full content
      const canonicalUrl = getCanonicalUrl(`blog/${slug}`, currentLanguage);
      const metaKeywords = `KI Videoproduktion, AI Video Marketing, Future Level, KI Videos Schweiz, ${postFromData.title[currentLanguage]}, ${slug.replace(/-/g, ' ')}`;
      
      updateMetaTags({
        title: `${postFromData.title[currentLanguage]} | Future Level - KI Video Agentur`,
        description: postFromData.excerpt[currentLanguage],
        keywords: metaKeywords,
        canonicalUrl: canonicalUrl,
        language: currentLanguage,
        ogImage: postFromData.image,
        ogType: "article"
      });
      
      // Add schema.org markup for basic blog posts too
      generateArticleSchema(
        postFromData.title[currentLanguage],
        postFromData.excerpt[currentLanguage],
        postFromData.image,
        postFromData.date,
        canonicalUrl,
        currentLanguage,
        metaKeywords
      );
    }
  }, [postFromData, articleContent, currentLanguage, navigate, slug, getLocalizedPath]);
  
  if (!postFromData && !articleContent) return null;
  
  const post = articleContent || postFromData;
  const postImage = articleContent ? articleContent.image : postFromData?.image;
  const postTitle = articleContent ? 
    articleContent.meta[currentLanguage].title : 
    postFromData?.title[currentLanguage];
  const postDate = articleContent ? articleContent.date : postFromData?.date;
  
  // Function to handle fallback image if main image fails
  const handleImageError = () => {
    console.error("Image failed to load:", postImage);
    setImageError(true);
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-[#121212]">
      <Header />
      <main className="flex-grow py-16 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]" itemScope itemType="https://schema.org/Article">
        <div className="container mx-auto px-4">
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link to={getLocalizedPath('/')} className="text-purple-400 hover:underline">
                  {t.home}
                </Link>
              </li>
              <li>
                <span className="text-gray-400 mx-2">/</span>
                <Link to={getLocalizedPath('blog')} className="text-purple-400 hover:underline">
                  {t.blog}
                </Link>
              </li>
              <li>
                <span className="text-gray-400 mx-2">/</span>
                <span className="text-gray-300" itemProp="headline">{postTitle}</span>
              </li>
            </ol>
          </nav>
          
          <div className="max-w-4xl mx-auto">
            {/* Enhanced image with better error handling */}
            <div className="w-full h-72 md:h-96 rounded-xl mb-8 shadow-lg overflow-hidden">
              <img 
                src={imageError ? fallbackImage : postImage || fallbackImage}
                alt={postTitle || "Blog post featured image"}
                className="w-full h-full object-cover transition-opacity duration-300"
                itemProp="image"
                onError={handleImageError}
              />
            </div>
            
            <div className="glass-card rounded-xl p-8 shadow-lg mb-6">
              <time dateTime={postDate} className="text-sm text-purple-300" itemProp="datePublished">
                {postDate}
              </time>
              
              {articleContent ? (
                <div 
                  className="prose prose-invert max-w-none mt-6 text-gray-100" 
                  dangerouslySetInnerHTML={{ __html: articleContent.content[currentLanguage] }}
                  itemProp="articleBody"
                />
              ) : (
                <>
                  <h1 className="text-3xl md:text-4xl font-bold mt-2 mb-6 text-white" itemProp="headline">{postTitle}</h1>
                  
                  <div className="prose prose-invert max-w-none text-gray-300" itemProp="articleBody">
                    <p className="text-lg mb-6">{postFromData?.excerpt[currentLanguage]}</p>
                    
                    <p className="mb-4">
                      {t.blogContentPlaceholder}
                    </p>
                    
                    <h2 className="text-2xl font-bold mt-8 mb-4 text-white">{t.blogSubheading1}</h2>
                    <p className="mb-4">
                      {t.blogContentPlaceholder}
                    </p>
                    
                    <h2 className="text-2xl font-bold mt-8 mb-4 text-white">{t.blogSubheading2}</h2>
                    <p className="mb-4">
                      {t.blogContentPlaceholder}
                    </p>
                    
                    <h2 className="text-2xl font-bold mt-8 mb-4 text-white">{t.blogSubheading3}</h2>
                    <p className="mb-4">
                      {t.blogContentPlaceholder}
                    </p>
                    
                    <div className="mt-12 p-6 bg-[#1A1F2C]/80 rounded-lg border border-purple-500/20">
                      <h3 className="text-xl font-semibold mb-4 text-white">{t.blogCTA}</h3>
                      <Link to={getLocalizedPath('contact')} className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                        {t.contactUs}
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
